(() => {
  const core = window.FillerBaySearchCore, config = window.fbCatalogueConfig;
  if (!core || !config) return;

  const CACHE_KEY = 'fb-catalogue-search-v5';
  const CACHE_TTL = 5 * 60 * 1000;
  let loading;

  function cacheKey() {
    const currency = window.Shopify?.currency?.active || window.Shopify?.currency || '';
    const country = window.Shopify?.country || '';
    return CACHE_KEY + ':' + currency + ':' + country;
  }

  function readCache() {
    try {
      const raw = sessionStorage.getItem(cacheKey());
      if (!raw) return null;
      const cached = JSON.parse(raw);
      if (!cached || !Array.isArray(cached.products) || Date.now() - cached.time > CACHE_TTL) return null;
      return cached.products.map(product => core.prepare(product, config.products[product.handle]));
    } catch (_) {
      return null;
    }
  }

  function writeCache(products) {
    try {
      sessionStorage.setItem(cacheKey(), JSON.stringify({time: Date.now(), products}));
    } catch (_) {}
  }

  document.addEventListener('submit', event => {
    const form = event.target.closest('predictive-search form');
    if (!form) return;
    const input = form.querySelector('input[name="q"]');
    if (!input || input.value.trim()) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    input.focus();
  }, true);

  async function index() {
    const cached = readCache();
    if (cached) return cached;

    if (!loading) loading = (async () => {
      const fetchPage = async page => {
        const url = new URL(config.indexUrl, location.origin);
        url.searchParams.set('view', 'fb-index');
        url.searchParams.set('page', page);
        const response = await fetch(url, {
          headers: {Accept: 'text/html'},
          credentials: 'same-origin'
        });
        if (!response.ok) throw new Error('Catalogue unavailable');
        const data = await response.json();
        if (!Array.isArray(data.products)) throw new Error('Invalid catalogue response');
        return data;
      };

      const first = await fetchPage(1);
      const rest = await Promise.all(
        Array.from({length: Math.max(0, first.pages - 1)}, (_, i) => fetchPage(i + 2))
      );
      const seen = new Set();
      const rawProducts = [first, ...rest]
        .flatMap(page => page.products)
        .filter(product => !seen.has(product.id) && seen.add(product.id));

      writeCache(rawProducts);
      return rawProducts.map(product => core.prepare(product, config.products[product.handle]));
    })().catch(error => {
      loading = null;
      console.warn('Filler Bay catalogue search:', error.message);
      throw error;
    });

    return loading;
  }

  function element(tag, className, text) {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  }

  function card(product) {
    const li = element('li', 'fb-search-card');
    const link = element('a', 'fb-search-card__link');
    link.href = product.url;

    if (product.image) {
      const picture = element('img');
      picture.src = product.image;
      picture.alt = product.title;
      picture.loading = 'lazy';
      picture.width = 440;
      picture.height = 440;
      link.append(picture);
    }

    link.append(element('span', 'fb-search-card__title', product.title));

    if (product.hasBulkDiscount && product.lowestMoney) {
      const priceWrap = element('span', 'fb-search-card__price-wrap');
      priceWrap.append(element('span', 'fb-search-card__price-label', 'As low as'));
      priceWrap.append(element('span', 'fb-search-card__price money', product.lowestMoney));
      link.append(priceWrap);
    } else {
      link.append(element('span', 'fb-search-card__price money', product.money));
    }

    if (!product.available) link.append(element('span', 'fb-search-card__stock', config.text.soldOut));
    li.append(link);
    return li;
  }

  function refreshSearchCurrency(attempt = 0) {
    const selector = document.querySelector('[name="doubly-currencies"]');
    const converter = window.DoublyGlobalCurrency;
    if (selector && typeof converter?.convertAll === 'function') {
      converter.convertAll(selector.value);
    } else if (attempt < 20) {
      setTimeout(() => refreshSearchCurrency(attempt + 1), 250);
    }
  }

  function results(hits, query, limit) {
    const wrapper = element('div', 'fb-search-results');
    const count = element('p', 'fb-search-count', config.text.results.replace('[count]', hits.length));
    count.setAttribute('role', 'status');
    wrapper.append(count);

    const list = element('ul', 'fb-search-grid');
    list.setAttribute('aria-label', config.text.resultsLabel);
    hits.slice(0, limit).forEach(product => list.append(card(product)));
    wrapper.append(list);

    if (!hits.length) wrapper.append(element('p', 'fb-search-empty', config.text.empty));
    return wrapper;
  }

  window.FillerBayCatalogue = {
    index,
    async suggestions(container, query, isCurrent) {
      const hits = core.search(await index(), query);
      if (!isCurrent()) return false;

      const wrapper = results(hits, query, 12);
      if (hits.length) {
        const link = element(
          'a',
          'results__search-btn fb-search-all',
          config.text.viewAll.replace('[count]', hits.length)
        );
        link.href = config.searchUrl + '?type=product&q=' + encodeURIComponent(query);
        wrapper.append(link);
      }

      container.replaceChildren(wrapper);
      refreshSearchCurrency();
      return true;
    }
  };

  customElements.whenDefined('predictive-search').then(() => {
    const prototype = customElements.get('predictive-search').prototype;
    const nativeSearch = prototype.getSearchResults;

    prototype.getSearchResults = async function(query, version = this.searchVersion) {
      const current = () => version === this.searchVersion && this.input.value.trim() === query;
      this.predictiveSearchResults.setAttribute('aria-busy', 'true');

      try {
        if (await window.FillerBayCatalogue.suggestions(this.predictiveSearchResults, query, current)) {
          this.open();
        }
      } catch (_) {
        if (current()) await nativeSearch.call(this, query, version);
      } finally {
        if (current()) this.predictiveSearchResults.removeAttribute('aria-busy');
      }
    };
  });

  async function fullSearch() {
    const target = document.querySelector('[data-fb-full-search]');
    if (!target) return;

    const query = new URLSearchParams(location.search).get('q') || '';
    if (!query.trim()) return;

    const native = document.querySelector('[data-fb-native-search]');
    const loadingMessage = element('p', 'fb-search-loading', 'Finding matching products…');

    native?.setAttribute('hidden', '');
    target.replaceChildren(loadingMessage);
    target.hidden = false;

    try {
      let hits = core.search(await index(), query);
      let visible = 32;

      const toolbar = element('div', 'fb-search-toolbar');
      const label = element('label', '', config.text.sort);
      label.htmlFor = 'fb-search-sort';

      const select = element('select');
      select.id = 'fb-search-sort';
      [
        ['relevance', config.text.relevance],
        ['price-up', config.text.priceUp],
        ['price-down', config.text.priceDown],
        ['title', config.text.alphabetical]
      ].forEach(([value, text]) => {
        const option = element('option', '', text);
        option.value = value;
        select.append(option);
      });
      toolbar.append(label, select);

      const content = element('div');
      const more = element('button', 'fb-search-more', config.text.showMore);
      more.type = 'button';

      const render = () => {
        content.replaceChildren(results(hits, query, visible));
        refreshSearchCurrency();
        more.hidden = visible >= hits.length;
      };

      window.fbLoadedSearchProducts = await index();

      select.addEventListener('change', () => {
        hits = core.search(window.fbLoadedSearchProducts, query);
        const effectivePrice = product =>
          Number(product.lowestPrice ?? product.price ?? 0);

        if (select.value === 'price-up') hits.sort((a, b) => effectivePrice(a) - effectivePrice(b));
        if (select.value === 'price-down') hits.sort((a, b) => effectivePrice(b) - effectivePrice(a));
        if (select.value === 'title') hits.sort((a, b) => a.title.localeCompare(b.title));

        visible = 32;
        render();
      });

      more.addEventListener('click', () => {
        visible += 32;
        render();
      });

      target.replaceChildren(toolbar, content, more);
      render();
    } catch (_) {
      target.hidden = true;
      native?.removeAttribute('hidden');
    }
  }

  // Warm the catalogue immediately so predictive results are ready before the customer types.
  index().catch(() => {});

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', fullSearch);
  } else {
    fullSearch();
  }
})();